/**
 * The battle report at the sector seam (the coordinator's row, Oct 10): when a battle ends — full or surrogate — one
 * card says what happened in the sector's words before the sector resumes: who held the field and what changed hands,
 * who fell (by name, yours and theirs), what is left lying there (wrecks, and what they would salvage for), the
 * ammunition each side has left or ran out of, how long it took, and the chronicle's decisive line. It is the record
 * the battle log and the chronicle already keep (`SectorState.battles`), presented once. Read through the viewer: a
 * battle it fought shows in full; one it only heard (a loud battle) shows the outcome, not the names; one it neither
 * fought nor heard is not reported. Pure.
 */
import type { BattleRecord, FactionId, SectorState } from './types';
import { recapLines } from './chronicle';
import { wreckValue } from './salvage';
import { tableFromUnits, type ScoreTable } from '../core/scoreboard';

export interface BattleReport {
  index: number;
  title: string;
  /** One line: the result in the sector's words. */
  headline: string;
  /** Good or bad for the viewer (or neither, a battle it only heard). */
  tone: 'won' | 'lost' | 'neutral';
  lines: Array<{ label: string; text: string }>;
  /** The scoreboard's table for this battle (67.14's shape), for a battle the viewer fought; null for one only heard. */
  table: ScoreTable | null;
}

/** Does the viewer know of this battle (fought it, or heard it go loud)? */
export function knowsOfBattle(state: SectorState, rec: BattleRecord, viewer: FactionId): 'fought' | 'heard' | null {
  if (rec.setup.sides.some(s => s.faction === viewer)) return 'fought';
  if (!rec.outcome.wentLoud) return null;
  const known = state.knowledge[viewer]?.systems[rec.setup.systemId];
  return known && known.seenDay >= rec.setup.day - 1e-9 ? 'heard' : null;
}

/** The report of `state.battles[index]` as `viewer` knows it, or null when it knows nothing of it. */
export function battleReport(state: SectorState, index: number, viewer: FactionId): BattleReport | null {
  const rec = state.battles[index];
  if (!rec) return null;
  const how = knowsOfBattle(state, rec, viewer);
  if (!how) return null;
  const { setup, outcome } = rec;
  const sys = state.systems.find(s => s.id === setup.systemId);
  const sysName = sys?.name ?? setup.systemId;
  const name = (f: FactionId | null | undefined) => (f ? state.factions.find(x => x.id === f)?.name ?? f : 'nobody');
  const kind = setup.opening.infiltration ? 'Infiltration' : 'Battle';
  const length = outcome.clock === 'action' ? `${Math.max(1, Math.round(outcome.seconds ?? 0))} s of fighting` : `${outcome.rounds} round${outcome.rounds === 1 ? '' : 's'}`;
  const winner = outcome.winner;
  const tone: BattleReport['tone'] = how === 'heard' ? 'neutral' : winner === viewer ? 'won' : winner === null ? 'neutral' : 'lost';
  // what changed hands: the capture logged at this battle's landing
  const landed = state.log.find(e => e.kind === 'captured' && e.systemId === setup.systemId && e.day >= setup.day - 1e-9 && !/has fallen/.test(e.text));
  const before = setup.opening.defender;
  const headline = winner
    ? `${name(winner)} ${landed && landed.faction === winner && before !== winner ? `takes ${sysName}` : winner === before ? `holds ${sysName}` : `wins at ${sysName}`}`
    : `No decision at ${sysName}${outcome.reason ? ` (${outcome.reason})` : ''}`;
  const lines: BattleReport['lines'] = [];
  const sides = setup.sides.map(s => {
    const units = outcome.units.filter(u => s.units.some(x => x.unitId === u.unitId));
    const fell = units.filter(u => !u.alive);
    const out = units.filter(u => u.extracted);
    return { side: s, units, fell, out };
  });
  lines.push({ label: 'sides', text: sides.map(x => `${name(x.side.faction)} ${x.side.units.length}${x.side.faction === before ? ' (holding)' : ''}`).join(' v ') });
  lines.push({ label: 'length', text: length });
  for (const x of sides) {
    const names = new Map(x.side.units.map(u => [u.unitId, u.name] as const));
    const lost = how === 'fought' && x.fell.length ? `: ${x.fell.map(u => names.get(u.unitId) ?? u.unitId).join(', ')}` : '';
    lines.push({ label: x.side.faction === viewer ? 'you lost' : `${name(x.side.faction)} lost`, text: `${x.fell.length} of ${x.side.units.length}${lost}${x.out.length ? `; ${x.out.length} got out` : ''}` });
  }
  if (how === 'fought') {
    // what lies there: the wrecks this battle left, and what they would salvage for
    const wrecks = outcome.wrecks ? Object.entries(outcome.wrecks).flatMap(([, byKit]) => Object.entries(byKit ?? {})) : [];
    const n = wrecks.reduce((a, [, k]) => a + k, 0);
    if (n) {
      const worth = sys ? wrecks.reduce((a, [kit, k]) => a + k * wreckValue(kit, sys, state.rules), 0) : 0;
      lines.push({ label: 'wrecks', text: `${n} left at ${sysName}${state.rules.salvage ? ` — worth ${worth.toFixed(0)} supply salvaged by whoever rests there holding it` : ''}` });
    }
    // ammunition: what each side has left, and who ran dry
    if (outcome.ammoLeft || outcome.shortage) {
      for (const x of sides) {
        const left = outcome.ammoLeft?.[x.side.faction];
        const dry = outcome.shortage?.[x.side.faction];
        if (left === undefined && !dry) continue;
        lines.push({ label: x.side.faction === viewer ? 'your rounds' : `${name(x.side.faction)} rounds`, text: `${left !== undefined ? `${left} a unit left` : ''}${dry ? `${left !== undefined ? '; ' : ''}${dry} ran dry` : ''}` });
      }
    }
    // the chronicle's decisive line: the kill that mattered most, else the last word
    if (outcome.chronicle?.length) {
      const names = new Map(setup.sides.flatMap(x => x.units.map(u => [u.unitId, u.name] as const)));
      const recap = recapLines(outcome.chronicle, outcome.clock, id => names.get(id) ?? id);
      const kills = recap.filter(l => /down|kill|falls/i.test(l));
      const pick = kills.length ? kills[kills.length - 1] : recap[recap.length - 1];
      if (pick) lines.push({ label: 'decisive', text: pick });
    }
  } else {
    lines.push({ label: 'heard', text: 'a loud battle, heard from where you watch; you know the outcome, not who fell' });
  }
  if (landed && landed.faction !== before) lines.push({ label: 'changes hands', text: `${sysName} is now ${name(landed.faction)}'s` });
  // the same table the scoreboard shows, from the units' record the battle's match wrote (one record, two screens)
  const table = how === 'fought'
    ? tableFromUnits(setup.sides.map((s, t) => ({ team: t, name: name(s.faction), units: s.units.map(u => ({ id: u.unitId, name: u.name })) })), outcome.units, setup.sides.findIndex(s => s.faction === viewer), outcome.clock === 'action' ? (outcome.seconds ?? 0) : outcome.rounds)
    : null;
  // a turn-clock battle's length is rounds, not a clock
  if (table && outcome.clock !== 'action') table.clock = `${outcome.rounds} round${outcome.rounds === 1 ? '' : 's'}`;
  return { index, title: `${kind} at ${sysName} · day ${setup.day.toFixed(1)}`, headline, tone, lines, table };
}

/** The reports of every battle from `fromIndex` on that the viewer knows of, oldest first. */
export function newBattleReports(state: SectorState, fromIndex: number, viewer: FactionId): BattleReport[] {
  const out: BattleReport[] = [];
  for (let i = Math.max(0, fromIndex); i < state.battles.length; i++) { const r = battleReport(state, i, viewer); if (r) out.push(r); }
  return out;
}
